import { createI18n } from "vue-i18n";

// Step 1: Define original messages (before lowering keys)
const rawMessages = {
  en: {
    // General UI/alerts
    appName: "Capena Client",
    theme: "Theme",
    environment: "Environment",
    selectLanguage: "Select Language",
    close: "Close",
    back: "Back",
    alertDetails: "Alert Details",
    alertsList: "Alerts List",
    noAlerts: "No alerts found",
    maintenance: "Maintenance",
    light: "Light",
    dark: "Dark",
    alertRemoved: "Alert removed",
    loadingAlerts: "Loading alerts...",
    alertRemovedWithId: "Alert removed. Identifier: {identifier}",

    // Login
    "login.title": "Welcome back",
    "login.subtitle": "Please sign in to continue",
    "login.username": "Username",
    "login.password": "Password",
    "login.signIn": "Sign In",
    "logout": "Logout",
    "login.httpsNote": "HTTPS required • Secure connection",
    "login.usernameRequired": "Username is required",
    "login.passwordRequired": "Password is required",
    "login.invalidCredentials": "Invalid credentials",
    "login.failed": "Login failed. Please try again.",

    // Table Headers/Descriptions
    id: "ID",
    idHeader: "Alert ID",
    idDescription: "Unique identifier for the alert",
    sender: "Sender",
    senderHeader: "Sender",
    senderDescription: "Source of the alert",
    received: "Received",
    receivedHeader: "Received Time",
    receivedDescription: "Time the alert was received",
    type: "Type",
    typeHeader: "Alert Type",
    typeDescription: "Type/category of the alert",
    status: "Status",
    statusHeader: "Status",
    statusDescription: "Current status of the alert",
    headline: "Headline",
    headlineHeader: "Headline",
    headlineDescription: "Short summary of the alert",
    messageSize: "Message Size",
    messageSizeHeader: "Message Size",
    messageSizeDescription: "Size of the alert message (bytes)",
    layer: "Layer",
    layerHeader: "Layers",
    layerDescription: "Geographic layer of the alert",
    severity: "Severity",
    severityHeader: "Severity",
    severityDescription: "Severity level of the alert",

    // Severity/Status/Type Values
    extreme: "Severe",
    actual: "Actual",
    exercise: "Exercise",
    test: "test",
    unknown: "unknown",

    // Time Units
    seconds: "seconds",
    minutes: "minutes",
    hours: "hours",
    days: "days",
    lastXMinutes: "Last {minutes} Min",
    avgRate: "Avg Rate",
    perMinute: "/ min",

    // Capena/Status
    "capena.connected": "Connected",
    "capena.disconnected": "Disconnected",
    "capena.connection": "Server Connection",
    "capena.lastKeepAlive": "Last keep alive",
    "capena.noKeepAlive": "No keep alive received",

    // API Manager
    "apiManager.title": "API Manager",
    "apiManager.shapefile": "Shapefile",
    "apiManager.requestShapefile": "Request Shapefile Content",
    "apiManager.updateVersion": "Update Version",
    "apiManager.version": "Version",
    "apiManager.requestId": "Request ID",
    "apiManager.requestIdDescription":
      "This ID helps track your request and match responses",
    "apiManager.switchedToShapefileTab":
      "Switched to Shapefile tab to show response",
    "apiManager.switchedToHistoryTab":
      "Switched to History tab to show response",
    "apiManager.history": "History",
    "apiManager.requestHistory": "Request History",
    "apiManager.fromDate": "From Date",
    "apiManager.toDate": "To Date",
    "apiManager.layerTypes": "Layer Types",
    "apiManager.category": "Category",
    "apiManager.event": "Event",
    "apiManager.responses": "Responses",
    "apiManager.shapefileContent": "Shapefile Content",
    "apiManager.status": "Status",
    "apiManager.link": "Link",
    "apiManager.versionResponse": "Version Response",
    "apiManager.historyRequest": "History Request",
    "apiManager.historyResponse": "History Response",
    "apiManager.date": "Date",
    "apiManager.time": "Time",
    "apiManager.errorMissingDates": "Please select both From and To dates.",
    "apiManager.errorInvalidDates": "Invalid date(s) selected.",
    "apiManager.errorFromAfterTo": "'From' date cannot be after 'To' date.",
    "apiManager.errorCategoryEvent":
      "Please select both category and event, or leave both empty.",
    "apiManager.errorInvalidVersion":
      "Version must be in the format number.number or number.number.number (e.g., 1.0 or 1.0.0)",

    // Layer Types
    ALL: "ALL",
    ALL_IL: "ALL_IL",
    AREAS: "AREAS",
    CITIES: "CITIES",
    SQUARES: "SQUARES",

    // Filters/Search
    advancedFilters: "Advanced Filters",
    filterByStatus: "Status",
    filterByType: "Type",
    filterBySeverity: "Severity",
    filterByLayer: "Layer",
    clearAllFilters: "Clear All Filters",
    searchStatus: "Search status...",
    searchType: "Search type...",
    searchAlerts: "Search alerts...",
    searchSeverity: "Search severity...",
    searchLayer: "Search layer...",
    uniqueTypes: "Unique Types",
    uniqueStatuses: "Statuses",

    // Alert Details
    alertNotFound: "Alert not found",
    alertPassedTtl:
      "This alert has passed its TTL of {minutes} minutes and is no longer active",

    // Description
    description: "Description",
  },
  he: {
    // General UI/alerts
    appName: "לקוח קפנה",
    theme: "ערכת נושא",
    environment: "סביבה",
    selectLanguage: "בחר שפה",
    close: "סגור",
    back: "חזרה",
    alertDetails: "פרטי התרעה",
    alertsList: "רשימת התרעות",
    noAlerts: "לא נמצאו התרעות",
    maintenance: "תחזוקה",
    light: "בהיר",
    dark: "כהה",
    alertRemoved: "התרעה הוסרה",
    loadingAlerts: "טוען התרעות...",
    alertRemovedWithId: "התרעה הוסרה. מזהה: {identifier}",

    // Login
    "login.title": "ברוך שובך",
    "login.subtitle": "אנא התחבר/י להמשך",
    "login.username": "שם משתמש",
    "login.password": "סיסמה",
    "login.signIn": "כניסה",
    "logout": "התנתק",
    "login.httpsNote": "נדרש HTTPS • חיבור מאובטח",
    "login.usernameRequired": "שם משתמש הוא שדה חובה",
    "login.passwordRequired": "סיסמה היא שדה חובה",
    "login.invalidCredentials": "פרטי התחברות שגויים",
    "login.failed": "כניסה נכשלה. נסו שוב.",

    // כותרות טבלה / תיאורים
    id: "מזהה",
    idHeader: "מזהה התרעה",
    idDescription: "מזהה ייחודי של ההתרעה",
    sender: "שולח",
    senderHeader: "שולח",
    senderDescription: "מקור ההתרעה",
    received: "התקבל",
    receivedHeader: "זמן קבלת התרעה",
    receivedDescription: "זמן קבלת ההתרעה",
    type: "סוג",
    typeHeader: "סוג התרעה",
    typeDescription: "סוג/קטגוריה של ההתרעה",
    status: "סטטוס",
    statusHeader: "סטטוס",
    statusDescription: "סטטוס נוכחי של ההתרעה",
    headline: "כותרת",
    headlineHeader: "כותרת",
    headlineDescription: "תקציר קצר של ההתרעה",
    messageSize: "גודל הודעה",
    messageSizeHeader: "גודל הודעה",
    messageSizeDescription: "גודל הודעת ההתרעה (בתים)",
    layer: "שכבה",
    layerHeader: "שכבות",
    layerDescription: "שכבת גיאוגרפית של ההתרעה",
    severity: "חומרה",
    severityHeader: "חומרה",
    severityDescription: "רמת החומרה של ההתרעה",

    // Severity/Status/Type Values
    extreme: "חמור",
    actual: "אמיתי",
    exercise: "תרגיל",
    test: "בדיקה",
    unknown: "לא ידוע",

    // Time Units
    seconds: "שניות",
    minutes: "דקות",
    hours: "שעות",
    days: "ימים",
    lastXMinutes: "{minutes} דקות אחרונות",
    avgRate: "קצב ממוצע",
    perMinute: "/ דקה",

    // Capena/Status
    "capena.connected": "מחובר",
    "capena.disconnected": "מנותק",
    "capena.connection": "חיבור לקפנה",
    "capena.lastKeepAlive": "מסר חיות אחרון",
    "capena.noKeepAlive": "לא התקבל מסר חיות",

    // API Manager
    "apiManager.title": "ניהול API",
    "apiManager.shapefile": "קבצי שכבות",
    "apiManager.requestShapefile": "בקש תוכן קבצי שכבות",
    "apiManager.updateVersion": "עדכן גרסה",
    "apiManager.version": "גרסה",
    "apiManager.requestId": "מזהה בקשה",
    "apiManager.requestIdDescription":
      "מזהה זה עוזר לך לעקוב אחר הבקשה שלך ולהתאים תגובות",
    "apiManager.switchedToShapefileTab":
      "החלף לכרטיסיית קבצי שכבות כדי להציג את התגובה",
    "apiManager.switchedToHistoryTab":
      "החלף לכרטיסיית היסטוריה כדי להציג את התגובה",
    "apiManager.history": "היסטוריה",
    "apiManager.requestHistory": "בקש היסטוריה",
    "apiManager.fromDate": "מתאריך",
    "apiManager.toDate": "עד תאריך",
    "apiManager.layerTypes": "סוגי שכבות",
    "apiManager.category": "קטגוריה",
    "apiManager.event": "אירוע",
    "apiManager.responses": "תגובות",
    "apiManager.shapefileContent": "תוכן קבצי שכבות",
    "apiManager.status": "סטטוס",
    "apiManager.link": "קישור",
    "apiManager.versionResponse": "תגובת גרסה",
    "apiManager.historyRequest": "בקשת היסטוריה",
    "apiManager.historyResponse": "תגובת היסטוריה",
    "apiManager.date": "תאריך",
    "apiManager.time": "שעה",
    "apiManager.errorMissingDates":
      "אנא בחרו תאריכים עבור 'מתאריך' ו-'עד תאריך'.",
    "apiManager.errorInvalidDates": "נבחרו תאריכים לא תקינים.",
    "apiManager.errorFromAfterTo":
      "תאריך 'מתאריך' לא יכול להיות אחרי 'עד תאריך'.",
    "apiManager.errorCategoryEvent":
      "יש לבחור גם קטגוריה וגם אירוע, או להשאיר את שניהם ריקים.",
    "apiManager.errorInvalidVersion":
      "הגרסה חייבת להיות בפורמט מספר.מספר או מספר.מספר.מספר (למשל 1.0 או 1.0.0)",

    // Layer Types
    ALL: "כל הארץ",
    ALL_IL: "כל הארץ",
    AREAS: "איזורים",
    CITIES: "ערים",
    SQUARES: 'קמ"ר',

    // Filters/Search
    advancedFilters: "סינון מתקדם",
    filterByStatus: "סינון לפי סטטוס",
    filterByType: "סינון לפי סוג",
    filterBySeverity: "סינון לפי חומרה",
    filterByLayer: "סינון לפי שכבה",
    clearAllFilters: "נקה כל הסינונים",
    searchStatus: "חיפוש סטטוס...",
    searchType: "חיפוש סוג...",
    searchAlerts: "חיפוש התרעות",
    searchSeverity: "חיפוש חומרה...",
    searchLayer: "חיפוש שכבה...",
    uniqueTypes: "סוגים ייחודיים",
    uniqueStatuses: "סטטוסים",

    // Alert Details
    alertNotFound: "התרעה לא נמצאה",
    alertPassedTtl:
      "התרעה זו חלפה את זמן החיים שלה של {minutes} דקות ואינה פעילה יותר",

    // Description
    description: "תיאור",
  },
};

// Step 2: Convert all translation keys to lowercase
function lowercaseKeys(obj) {
  if (Array.isArray(obj)) {
    return obj.map(lowercaseKeys);
  } else if (obj !== null && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key.toLowerCase(),
        lowercaseKeys(value),
      ])
    );
  }
  return obj;
}

const messages = {
  en: lowercaseKeys(rawMessages.en),
  he: lowercaseKeys(rawMessages.he),
};

// Step 3: Create i18n instance
const i18n = createI18n({
  legacy: false,
  locale: "he", // Default locale
  fallbackLocale: "en",
  messages,
});

// Step 4: Patch `$t` to be case-insensitive
const originalT = i18n.global.t.bind(i18n.global);
i18n.global.t = function (key, ...args) {
  if (typeof key === "string") {
    return originalT(key.toLowerCase(), ...args);
  }
  return originalT(key, ...args);
};

export default i18n;
